import { useState } from "react";
import { AppInventoryItem } from "@/types/inventory";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppInventoryRow } from "./AppInventoryRow";
import { Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";

interface AppInventoryListProps {
  inventory: AppInventoryItem[];
}

export const AppInventoryList = ({ inventory }: AppInventoryListProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | "managed" | "unmanaged">("all");
  const [sortBy, setSortBy] = useState<"devices" | "name" | "recent">("devices");

  const filteredInventory = inventory.filter(app => {
    const term = searchTerm?.toLowerCase().trim() || "";
    const name = (app.name ?? "").toLowerCase();
    const bundle = (app.bundle_id ?? "").toLowerCase();

    const matchesSearch =
      name.includes(term) ||
      bundle.includes(term);
    
    const matchesFilter = 
      filterStatus === "all" ||
      (filterStatus === "managed" && app.is_managed) ||
      (filterStatus === "unmanaged" && !app.is_managed);
    
    return matchesSearch && matchesFilter;
  });

  const sortedInventory = [...filteredInventory].sort((a, b) => {
    switch (sortBy) {
      case "devices":
        return b.total_devices - a.total_devices;
      case "name":
        return a.name.localeCompare(b.name);
      case "recent":
        return new Date(b.last_updated || 0).getTime() - new Date(a.last_updated || 0).getTime();
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by app name or bundle ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Filter className="mr-2 h-4 w-4" />
                {filterStatus === "all" ? "All Apps" : filterStatus === "managed" ? "Managed" : "Unmanaged"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setFilterStatus("all")}>
                All Apps
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus("managed")}>
                Managed Only
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setFilterStatus("unmanaged")}>
                Unmanaged Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Sort by {sortBy === "devices" ? "Devices" : sortBy === "name" ? "Name" : "Recent"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setSortBy("devices")}>
                Device Count
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("name")}>
                Name
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortBy("recent")}>
                Recently Updated
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {sortedInventory.length > 0 ? (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Version</TableHead>
                <TableHead className="text-right">Devices</TableHead>
                <TableHead>First Seen</TableHead>
                <TableHead>Last Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedInventory.map((app) => (
                <AppInventoryRow key={app.bundle_id} app={app} />
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-muted-foreground">
            {searchTerm || filterStatus !== "all" 
              ? "No applications found matching your filters" 
              : "No applications discovered yet"}
          </p>
        </div>
      )}

      <div className="text-sm text-muted-foreground">
        Showing {sortedInventory.length} of {inventory.length} applications
      </div>
    </div>
  );
};
